import React, { useContext } from 'react';
import { Badge, Box, Button, ColumnLayout, Modal, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import {
    ColumnDef,
    ColumnSourceType,
    COADataKey,
    SegmentDataKeys,
} from 'src/models/segment-cdt/SegmentCdtTypes';
import {
    columnDisplayName,
    columnTypeLabel,
    ledgerLabel,
    periodBehaviorLabel,
    periodSummary,
} from 'src/components/segment-cdt/create/segmentCreateUtils';

/**
 * The COA dimensions a GL pull can filter, in the order the create form offers them, derived from
 * `COA_SEGMENT_MAPPING` rather than hand-listed so a dimension cannot be labelled one way here and another
 * way where it was configured. Mirrors `SegmentGlPullDataSource`'s own list.
 */
const M = CONSTANTS.COA_SEGMENT_MAPPING;
const DIMENSIONS: { ui: string, key: keyof SegmentDataKeys }[] = [
    { ui: M.COMPANY.UI, key: M.COMPANY.WEB_API as keyof SegmentDataKeys },
    { ui: M.GL_ACCOUNT.UI, key: M.GL_ACCOUNT.WEB_API as keyof SegmentDataKeys },
    { ui: M.LOCATION.UI, key: M.LOCATION.WEB_API as keyof SegmentDataKeys },
    { ui: M.COST_CENTER.UI, key: M.COST_CENTER.WEB_API as keyof SegmentDataKeys },
    { ui: M.PRODUCT_LINE.UI, key: M.PRODUCT_LINE.WEB_API as keyof SegmentDataKeys },
    { ui: M.CHANNEL.UI, key: M.CHANNEL.WEB_API as keyof SegmentDataKeys },
    { ui: M.PROJECT.UI, key: M.PROJECT.WEB_API as keyof SegmentDataKeys },
    { ui: M.LG_CODE.UI, key: M.LG_CODE.WEB_API as keyof SegmentDataKeys },
];

/** The two dimensions a GL pull always filters on, marked so the list distinguishes them from the rest. */
const REQUIRED_DIMENSIONS = [M.COMPANY.UI, M.GL_ACCOUNT.UI];

/** Both source types that pull a GL balance, and so carry pull criteria. Matches the backend's PULL_TYPES. */
function isGlPulling(sourceType?: ColumnSourceType): boolean {
    return sourceType === ColumnSourceType.PULL_GL || sourceType === ColumnSourceType.DERIVED_BAL;
}

function isDerived(sourceType?: ColumnSourceType): boolean {
    return sourceType === ColumnSourceType.DERIVED_PCT || sourceType === ColumnSourceType.DERIVED_BAL;
}

/**
 * A section heading with a hairline rule. Reuses the treatment built for the reconciliation table so the two
 * read as one feature. Renders nothing at all when it has no children, which is what keeps an inapplicable
 * section from leaving a bare heading behind.
 */
function Section(props: { title: string, children?: React.ReactNode }) {
    if (!props.children) {
        return null;
    }
    return (
        <div className="segmentColumnConfigSection">
            <div className="segmentColumnConfigSectionTitle">{props.title}</div>
            {props.children}
        </div>
    );
}

interface FieldSpec {
    key: string;
    label: string;
    value?: string;
    hint?: string;
}

/**
 * Keeps only the fields that actually carry a value.
 *
 * Filtering the SPECS rather than rendered elements is deliberate and was a real bug once: a component that
 * returns null still constructs a truthy React element, so filtering elements counted absent fields as
 * present and rendered a section heading over nothing.
 */
function populated(specs: FieldSpec[]): FieldSpec[] {
    return specs.filter((f) => !StringUtils.isNullOrEmpty(f.value));
}

/** One labelled value, with an optional plain-language gloss beneath it. */
function renderFields(specs: FieldSpec[]): React.ReactNode[] {
    return specs.map((f) => (
        <div key={f.key}>
            <Box variant="awsui-key-label">{f.label}</Box>
            <div>{f.value}</div>
            {f.hint && <Box variant="small" color="text-body-secondary">{f.hint}</Box>}
        </div>
    ));
}

/**
 * A custom rollup's label, `tableName : classification`.
 *
 * Both parts are optional on `COADataKey`, so they are joined only where present: interpolating them directly
 * would render the literal string "undefined" into a chip, which claims a classification exists and names it
 * wrongly. A rollup missing both degrades to the dimension's own row with an empty chip rather than a lie.
 *
 * `resolvedName` is the name looked up from the Custom CoA definitions, used only when the key itself carries
 * none — so the day the read path starts returning `tableName`, that wins and the lookup becomes redundant
 * rather than conflicting.
 */
function rollupLabel(key: COADataKey, resolvedName?: string): string {
    const name = StringUtils.isNullOrEmpty(key.tableName) ? resolvedName : key.tableName;
    return [name, key.classification].filter((part) => !StringUtils.isNullOrEmpty(part)).join(' : ');
}

/**
 * A custom rollup, named.
 *
 * <p>`GetSegmentDataTable` recomposes a rollup key from the stored criteria, which hold the Custom CoA's
 * **id** and classification but not its name — so the chip could only ever say "AEU", naming the slice of a
 * table without naming the table. The name is resolved here from the same list the pull form picks rollups out
 * of (`GetActiveCustomCOADefinitionsForSegment`), keyed by the dimension the rollup sits under: a rollup on
 * Cost Center is a Custom CoA defined for Cost Center.
 *
 * <p>Asks for nothing when there is no id to resolve, and shows the classification alone until the answer
 * arrives — which is what it said before, and true, so there is no wrong intermediate state to avoid. An id
 * that resolves to nothing (an archived definition) also stays at the classification: the uuid is not worth
 * showing here, since the classification already identifies the slice.
 */
function RollupChip(props: { dimensionUi: string, dataKey: COADataKey }) {
    const { dimensionUi, dataKey } = props;
    const context = useContext(SegmentTablesContext);
    // Nothing to look up when the key already names its table, or when it carries no id to look one up by:
    // a null argument leaves the hook idle rather than fetching an answer that would be discarded.
    const needsLookup = StringUtils.isNullOrEmpty(dataKey.tableName)
        && !StringUtils.isNullOrEmpty(dataKey.tableId);
    const [definitions] = context.services.customCOAService.getCustomCOADefinitionsForSegment(
        needsLookup ? dimensionUi : null,
    );
    const resolved = (definitions ?? []).find((d) => d.tableId === dataKey.tableId)?.tableName;
    return <Badge color="grey">{rollupLabel(dataKey, resolved)}</Badge>;
}

/**
 * One dimension's keys. A CUSTOM rollup renders as a chip rather than plain text because it is a reference to
 * another table, not a literal code, and reading it as a value would misrepresent what the pull filters on.
 */
function DataKeyValue(props: { dimensionUi: string, keys: COADataKey[] }) {
    return (
        <>
            {props.keys.map((key, i) => (
                <React.Fragment key={`${key.value ?? key.tableId}-${i}`}>
                    {i > 0 && ' '}
                    {key.type === CONSTANTS.COA_DATA_KEY_TYPE.CUSTOM
                        ? <RollupChip dimensionUi={props.dimensionUi} dataKey={key} />
                        : <span className="segmentColumnConfigCode">{key.value}</span>}
                </React.Fragment>
            ))}
        </>
    );
}

/**
 * Read-only configuration of one Segment Data Table column, opened from its grid heading.
 *
 * <p>Reads everything it needs from `SegmentTablesContext` (the open column, the table's column list, and
 * `dispatch`), mirroring `ColumnConfigTrigger`, so opening and closing are one round-trip through the reducer
 * rather than a prop chain back down from the view.
 *
 * <p>Derived entirely from the `ColumnDef` the definition already carries: no fetch, and nothing about the
 * column's run history. Progress and failures from a GL pull live on the metadata row's pull indicator, which
 * is where they stay current; duplicating them here would show a snapshot that silently goes stale.
 *
 * <p>The contract has no update-column operation - columns are add-only - so this is read-only by
 * construction rather than by choice.
 *
 * <p><b>An absent field renders no row, and a section with no rows renders no heading.</b> A `MANUAL` column
 * therefore collapses to its two short sections, which genuinely is its whole configuration. Blank-but-present
 * rows would imply the opposite, and a `0` substituted for an unset `defaultValue` would assert a value for
 * every unmatched grain rather than standing in for "none set".
 */
export default function SegmentColumnConfigModal() {
    const context = useContext(SegmentTablesContext);
    const dispatch = context?.dispatch;
    // Which column is open, and the sibling list used to resolve a derived column's references, both read from
    // the detail slice rather than taken as props: they are the same values the heading dispatched into it, and
    // threading them back down would be a second source of the same truth.
    const column = context?.state?.detail?.configColumn;
    const columns = context?.state?.detail?.definition?.columns ?? [];
    // Mount-gated: nothing is open (or there is no provider at all), so stay out of the tree entirely rather
    // than rendering a hidden shell (matching SegmentAddColumnModal).
    if (!dispatch || !column) {
        return null;
    }
    const onDismiss = () => dispatch(ACTIONS.SET_DETAIL_CONFIG_COLUMN.withPayload(undefined));

    const pc = column.pullCriteria;
    const dc = column.derivedConfig;
    const dataKeys = pc?.dataKeys;

    // Only dimensions the column actually filters on. The server rejects more than one key per dimension, but
    // the joined rendering handles a list anyway rather than assuming a shape the payload does not guarantee.
    const filledDimensions = DIMENSIONS
        .map((d) => ({ ...d, keys: (dataKeys?.[d.key] ?? []) as COADataKey[] }))
        .filter((d) => d.keys.length > 0);

    const behavior = periodBehaviorLabel(column.periodBehavior);

    const pullFields = populated([
        { key: 'ledger', label: 'Ledger', value: ledgerLabel(pc?.ledgerType) },
        { key: 'period', label: 'Period', value: periodSummary(pc) },
        { key: 'fxType', label: 'FX type', value: pc?.fxType },
        { key: 'fxDate', label: 'FX date', value: pc?.fxDate },
    ]);

    const derivedFields = populated([
        { key: 'src', label: 'Source column', value: columnDisplayName(dc?.sourceColumnId, columns) },
        { key: 'pct', label: 'Percentage column', value: columnDisplayName(dc?.pctColumnId, columns) },
        { key: 'val', label: 'Value column', value: columnDisplayName(dc?.valueColumnId, columns) },
    ]);

    const optionFields = populated([
        {
            key: 'default',
            label: 'Default value',
            value: column.defaultValue,
            // Kept to one short line: the hints sit in a half-width grid cell, and a three-line gloss reads
            // as cramped beside the single figure it explains.
            hint: 'Used in Common Balance Pool joins when a segment has no row here.',
        },
        { key: 'behavior', label: 'Period behaviour', value: behavior?.label, hint: behavior?.hint },
    ]);

    return (
        <Modal
            visible={true}
            onDismiss={onDismiss}
            size="medium"
            closeAriaLabel="Close column configuration"
            header={column.displayName ?? column.columnId}
            footer={<Box float="right"><Button variant="primary" onClick={onDismiss}>Close</Button></Box>}
        >
            <SpaceBetween size="l">
                {/* Identity: the type carries the colour, and data type / currency sit inline beside it. They
                    are one-word facts, so giving each a label and its own line would treble the height. */}
                <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                    <Badge color={column.sourceType === ColumnSourceType.PULL_GL ? 'blue' : 'grey'}>
                        {columnTypeLabel(column.sourceType)}
                    </Badge>
                    <span className="segmentColumnConfigMeta">
                        {[column.dataType, column.currency].filter((x) => !StringUtils.isNullOrEmpty(x)).join(' · ')}
                    </span>
                </SpaceBetween>

                {/* Full width and above the grouped sections: it is the only free text, and it is what a
                    reader wants first once they know which column they are looking at. */}
                {!StringUtils.isNullOrEmpty(pc?.description) && (
                    <div className="segmentColumnConfigDescription">{pc?.description}</div>
                )}

                {isGlPulling(column.sourceType) && pullFields.length > 0 && (
                    <Section title="GL Pull">
                        <ColumnLayout columns={2} variant="text-grid">{renderFields(pullFields)}</ColumnLayout>
                    </Section>
                )}

                {isDerived(column.sourceType) && derivedFields.length > 0 && (
                    <Section title="Derived from">
                        <ColumnLayout columns={2} variant="text-grid">{renderFields(derivedFields)}</ColumnLayout>
                    </Section>
                )}

                {filledDimensions.length > 0 && (
                    <Section title="Data keys (COA)">
                        <table className="segmentColumnConfigKeys">
                            <tbody>
                                {filledDimensions.map((d) => (
                                    <tr key={d.ui}>
                                        <td className="keyDimension">
                                            {d.ui}
                                            {REQUIRED_DIMENSIONS.indexOf(d.ui) >= 0 && (
                                                <span className="keyRequired" title="Required for a GL pull"> *</span>
                                            )}
                                        </td>
                                        <td className="keyValue">
                                            <DataKeyValue dimensionUi={d.ui} keys={d.keys} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Section>
                )}

                {optionFields.length > 0 && (
                    <Section title="Options">
                        <ColumnLayout columns={2} variant="text-grid">{renderFields(optionFields)}</ColumnLayout>
                    </Section>
                )}
            </SpaceBetween>
        </Modal>
    );
}

/**
 * Exported for the unit tests, which assert the dimension order and which two are marked required.
 *
 * Note this deliberately does NOT use `CommonDataSourceOperations.convertToDataKeyJoinedString`, the helper
 * the CLI views format data keys with. That returns one joined string, which cannot carry the distinction
 * between a literal code and a custom-rollup reference; rendering the rollup as a chip needs the structured
 * key. The `[tableName:classification]` wording it produces is preserved as the chip's own text.
 */
export const TEST_ONLY = { DIMENSIONS, REQUIRED_DIMENSIONS };
